import { type ClipboardEvent, useState } from "react"
import { flushSync } from "react-dom"
import { parseEmails } from "@/v2-backoffice-client/utils"

export type BulkRowBase = { key: string }
export type BulkRow<T> = T & BulkRowBase

export type BulkRowsOptions<T extends BulkRowBase> = {
  createEmptyRow(): T
  /** Used for one-cell pastes that look like a list (e.g. "a@x.com, b@y.com"). */
  parseClipboard?(text: string): string[]
}

export function useBulkRows<T extends BulkRowBase>(input: BulkRowsOptions<T>) {
  const { createEmptyRow, parseClipboard = parseEmails } = input
  const [rows, setRows] = useState<T[]>([createEmptyRow()])

  function addRow() {
    setRows((prev) => [...prev, createEmptyRow()])
  }

  function removeRow(index: number) {
    setRows((prev) => {
      // Last remaining row: clear it instead of leaving a no-op, so the
      // close button on the only row still does something useful.
      if (prev.length <= 1) return [createEmptyRow()]
      return prev.filter((_, i) => i !== index)
    })
  }

  function resetRows() {
    setRows([createEmptyRow()])
  }

  function updateRow<K extends keyof T>(index: number, field: K, value: T[K]) {
    setRows((prev) => {
      return prev.map((row, i) => {
        if (i === index) return { ...row, [field]: value }
        return row
      })
    })
  }

  // Generic table-paste handler. Wired at the form level: when the paste
  // originates in a text input inside a `<tr>`, we treat the clipboard as a
  // TSV grid and fill the matching `<input>` / `<textarea>` of each cell in
  // subsequent rows. Non-input cells (selects, custom pickers) keep their
  // defaults. We extend the row list as needed and trigger React updates by
  // dispatching native input events, so consumers don't need any per-field
  // wiring.
  function handlePaste(e: ClipboardEvent) {
    const target = e.target
    if (!isTextInput(target)) return

    const td = target.closest("td")
    const tr = td?.closest("tr") ?? null
    const tbody = tr?.parentElement ?? null
    if (!td || !tr || !tbody) return

    const text = e.clipboardData.getData("text/plain")
    let table = parseClipboardTable(text)

    // One pasted cell: try to expand it as a list (legacy email-list flow).
    if (table.length === 1 && table[0].length === 1) {
      const items = parseClipboard(table[0][0])
      if (items.length <= 1) return
      table = items.map((item) => [item])
    }

    e.preventDefault()

    const sourceRowIdx = indexOfChild(tbody, tr)
    const sourceColIdx = indexOfChild(tr, td)
    if (sourceRowIdx < 0 || sourceColIdx < 0) return

    const neededRows = sourceRowIdx + table.length

    flushSync(() => {
      setRows((prev) => {
        if (prev.length >= neededRows) return prev
        const extras = Array.from({ length: neededRows - prev.length }, () =>
          createEmptyRow(),
        )
        return [...prev, ...extras]
      })
    })

    // Now the new rows are in the DOM; fan the values out into them.
    for (let r = 0; r < table.length; r++) {
      const targetTr = tbody.children[sourceRowIdx + r]
      if (!targetTr) continue
      const cells = table[r]
      for (let c = 0; c < cells.length; c++) {
        const targetTd = targetTr.children[sourceColIdx + c]
        if (!targetTd) continue
        const cellInput = targetTd.querySelector<
          HTMLInputElement | HTMLTextAreaElement
        >("input, textarea")
        if (!cellInput) continue
        setNativeValue(cellInput, cells[c])
      }
    }
  }

  return {
    rows,
    setRows,
    addRow,
    removeRow,
    resetRows,
    updateRow,
    handlePaste,
  }
}

function indexOfChild(parent: Element, child: Element): number {
  return Array.prototype.indexOf.call(parent.children, child)
}

function isTextInput(
  node: EventTarget | null,
): node is HTMLInputElement | HTMLTextAreaElement {
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
}

export function parseClipboardTable(text: string): string[][] {
  const normalized = text.replace(/\r\n?/g, "\n")
  const lines = normalized.split("\n")
  while (lines.length > 0 && lines[lines.length - 1] === "") {
    lines.pop()
  }
  return lines.map((line) => line.split("\t"))
}

// React tracks input values internally; the only reliable way to programmatically
// fire `onChange` on a controlled input is to bypass the React tracker via the
// prototype setter and dispatch a native `input` event.
export function setNativeValue(
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string,
) {
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set
  if (!setter) return
  setter.call(el, value)
  el.dispatchEvent(new Event("input", { bubbles: true }))
}
